/**
 * @module builder
 */
import type {
  AnyPresentationOption,
  OptionsDocument,
  PresentationOption,
  PresentationOptionV2,
  QuoteDocument
} from 'paintscout'

import get from 'lodash/get'
import uniq from 'lodash/uniq'
import { getQuoteType } from '../../../quote/util'
import { getDefaultPresentation } from '../get-default-presentation'

export interface GetPresentationOptionsArgs {
  options: OptionsDocument
  quoteType?: string
  inactive?: boolean
  quote?: QuoteDocument
  /** Read the v2 library (`options.presentationOptionsV2`) instead of the v1 one. */
  v2?: boolean
}

/**
 * Lists a presentation library. Overloaded on `v2` so callers get the right document shape back
 * without casting — the two libraries hold structurally different trees (#21990).
 *
 * NOTE the `v2: true` overload asserts rather than proves: it reads whatever is in the v2 map, so a
 * doc written there without `schemaVersion` would be mistyped. Everything that writes that map
 * (createPresentationNextOption, convertPresentationV1ToV2) stamps it. Use getPresentationNextOptions
 * when you need membership actually verified.
 */
export function getPresentationOptions(args: GetPresentationOptionsArgs & { v2: true }): PresentationOptionV2[]
export function getPresentationOptions(args: GetPresentationOptionsArgs & { v2?: false }): PresentationOption[]
export function getPresentationOptions(args: GetPresentationOptionsArgs): AnyPresentationOption[]
export function getPresentationOptions(args: GetPresentationOptionsArgs): AnyPresentationOption[] {
  const { options, inactive, v2 } = args
  const optionsKey = v2 ? 'presentationOptionsV2' : 'presentationOptions'

  const presentationOptions = get(options.options, optionsKey, {
    order: [],
    values: {} as { [key: string]: PresentationOption },
    quoteTypeOrder: {} as { [key: string]: string[] }
  })

  const quoteType = getQuoteType(args)

  let order =
    presentationOptions.quoteTypeOrder && presentationOptions.quoteTypeOrder[quoteType]
      ? uniq([...presentationOptions.quoteTypeOrder[quoteType], ...presentationOptions.order])
      : uniq(presentationOptions.order)

  if (!order) {
    order = []
  }

  const r = order
    .map((orderItem: string) => {
      const item = presentationOptions.values[orderItem] as PresentationOption
      if (!item) {
        return null
      }

      // v2 docs have no int_ext field at all, so an absent quoteTypes means "all types"
      // rather than falling back to a legacy v1-only field.
      let quoteTypes
      if (item.quoteTypes) {
        quoteTypes = item.quoteTypes
      } else if (!v2) {
        quoteTypes = [get(item, 'int_ext.value')]
      } else {
        quoteTypes = []
      }

      const option: PresentationOption = {
        ...item,
        key: orderItem,
        quoteTypes
      }

      if (!inactive && item.active !== true && typeof item.active !== 'undefined') {
        return null
      } else if (inactive) {
        option.active = typeof item.active === 'undefined' ? true : !!item.active
      }

      if (
        !quoteType ||
        quoteType === 'all' ||
        quoteTypes.includes('all') ||
        quoteTypes.includes(quoteType) ||
        !quoteTypes.length //If no quoteTypes are selected, show this option.
      ) {
        return option
      }

      return null
    })
    .filter((item) => item)

  // Only include default if its not saved as a presentation already
  if (r.find((item) => item.key === 'default')) {
    return [...r]
  } else {
    return [getDefaultPresentation({ options }), ...r]
  }
}
